import { format } from 'date-fns'
import React, { useRef } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useReactToPrint } from 'react-to-print'
import ModalComponent from '..'
import { SKELETON_BASE_COLOR } from '../../../constants'
import { formatCurrency } from '../../../helpers'
import { usePageLeave } from '../../../hooks/usePageLeave'
import { useGetHarmonizedOrdersBreakdownQuery } from '../../../redux-toolkits/orders/orders.slice'
import { Button } from '../../button'
import { OrderItem } from '../../product/orderItem'

export const POSOrderReceiptModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  fulfilmentCentre?: string
  harmonizedReference: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  harmonizedReference,
  fulfilmentCentre,
}) => {
  const printRef = useRef<HTMLDivElement>(null)

  const { data: harmonizedOrder, isFetching } =
    useGetHarmonizedOrdersBreakdownQuery(
      {
        harmonizedReference: harmonizedReference ?? '',
        fulfilmentCentre,
      },
      { refetchOnMountOrArgChange: true, skip: !open },
    )

  usePageLeave({
    callback: () => {
      onCloseCallback()
    },
    showDialog: true,
  })

  const handlePrintPkg = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'order-receipt',
    onAfterPrint: () => {},
    onBeforePrint: () => {
      return Promise.resolve()
    },
    bodyClass: '!p-6',
  })

  const handlePrintCustom = () => {
    const content = printRef.current
    if (!content) return

    const printWindow = window.open('', '', 'width=800,height=600')

    if (!printWindow) return

    printWindow.document.write(`
      <html>
        <head>
          <title>Print</title>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body class="p-6">
          <div id="print-root">
            ${content.innerHTML}
          </div>
        </body>
      </html>
    `)

    printWindow.document.close()

    // Wait for Tailwind to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 500) // give time for images and styles to load
    }
  }

  const handlePrint = (usePkg: boolean = false) => {
    if (usePkg) {
      handlePrintPkg()
    } else {
      handlePrintCustom()
    }
  }

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback()
      }}
      title={undefined}
      childrenClassName="md:!max-w-[420px]"
    >
      <>
        <div
          ref={printRef}
          className="flex flex-col items-center justify-center space-y-6"
        >
          <div className="flex flex-col space-y-1 items-center justify-center text-center text-sec-black w-full">
            <img
              src="/assets/images/good-logo.svg"
              alt="shopping-bags"
              className="block max-w-[80px] w-full"
            />

            {harmonizedOrder?.data?.orders[0]?.isPOSOrder ||
            harmonizedOrder?.data?.orders[0]?.delivery?.type === 'PICKUP' ? (
              <p className="text-sm">
                {harmonizedOrder?.data?.orders[0]?.fulfilmentCentre?.address}
              </p>
            ) : undefined}

            <p className="text-sm">+234 814 251 1154</p>

            {!!harmonizedOrder?.data?.orders[0]?.createdAt && (
              <p className="text-sm">
                {format(
                  harmonizedOrder?.data?.orders[0]?.createdAt,
                  'EEE MMM do, hh:mm a',
                )}
              </p>
            )}
          </div>

          <div className="text-base flex flex-col space-y-1 w-full">
            <p className="flex items-center justify-between space-x-2 w-full">
              <span className="font-medium">Order Reference</span>
              <span className="text-sec-black">#{harmonizedReference}</span>
            </p>
          </div>

          {isFetching ? (
            <div className="flex flex-col space-y-4 w-full">
              {[1, 2, 3, 4, 5, 6].map((i) => {
                return (
                  <Skeleton
                    width={'100%'}
                    height={'60px'}
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_BASE_COLOR}
                    className="!rounded-xl aspect-square"
                    key={`${i}`}
                  />
                )
              })}
            </div>
          ) : (
            <>
              {harmonizedOrder?.data?.orders?.map((order, idx) => {
                return (
                  <div
                    className="flex flex-col space-y-6 w-full"
                    key={order.orderNumber}
                  >
                    {order.orderItems?.map((item, idx) => {
                      const key = `item-${item._id}-${idx}`

                      return (
                        <OrderItem
                          item={item}
                          showImage={false}
                          key={key}
                          basePricePrefix=""
                          className="!max-w-full !py-0 !text-sm !border-0"
                          showPriceBreakdown={false}
                        />
                      )
                    })}
                  </div>
                )
              })}
            </>
          )}

          <div className="text-base flex flex-col space-y-1 w-full">
            <p className="flex items-center justify-between space-x-2 w-full">
              <span className="font-medium">
                Order value for selected centres
              </span>
              <span className="text-black">
                {formatCurrency(
                  harmonizedOrder?.data?.summary.orderValue ?? 0,

                  {
                    currency: 'NGN',
                    decimalPlaces: 2,
                  },
                )}
              </span>
            </p>
          </div>

          {harmonizedOrder?.data?.orders[0]?.isPOSOrder ? (
            <p className="text-sm text-sec-black w-full text-center capitalize">
              Fulfilment order receipt
            </p>
          ) : (
            <p className="text-sm text-sec-black w-full text-center capitalize">
              Order placed by{' '}
              {harmonizedOrder?.data?.orders[0]?.personalInformation?.firstName}{' '}
              {harmonizedOrder?.data?.orders[0]?.personalInformation?.lastName}
            </p>
          )}
        </div>

        <Button
          type="button"
          label="Send to printer"
          onClick={() => {
            handlePrint(false)
          }}
          primary
          className="w-full mt-4"
        />
      </>
    </ModalComponent>
  )
}
